import { useState, useEffect, useRef, useMemo } from "react";
import { Search, Plus, X } from "lucide-react";

const CATEGORIES = ["Dry Goods", "Dairy", "Produce", "Meat & Seafood", "Beverages", "Coffee", "Bakery", "Packaging", "Cleaning", "Consumables"];

export default function ItemPicker({ items, onPick }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [catFilter, setCatFilter] = useState("");
  const [recent, setRecent] = useState([]);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  // Load recent items from localStorage
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("tbb_inv_recent") || "[]");
      setRecent(saved);
    } catch { setRecent([]); }
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    let list = items;
    if (catFilter) list = list.filter(i => i.category === catFilter);
    if (!q.trim()) {
      // Show recent first, then alphabetical
      const recentSet = new Set(recent);
      const recentItems = list.filter(i => recentSet.has(i.sku)).sort((a, b) => recent.indexOf(a.sku) - recent.indexOf(b.sku));
      const rest = list.filter(i => !recentSet.has(i.sku)).sort((a, b) => a.name.localeCompare(b.name));
      return [...recentItems, ...rest].slice(0, 20);
    }
    const needle = q.toLowerCase();
    return list.filter(i =>
      i.name.toLowerCase().includes(needle) ||
      i.sku.toLowerCase().includes(needle)
    ).slice(0, 20);
  }, [items, q, catFilter, recent]);

  const pick = (item) => {
    // Update recent list
    const updated = [item.sku, ...recent.filter(s => s !== item.sku)].slice(0, 10);
    setRecent(updated);
    localStorage.setItem("tbb_inv_recent", JSON.stringify(updated));
    onPick(item);
    setQ("");
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative" data-testid="item-picker">
      {/* Category chips */}
      <div className="flex gap-1 overflow-x-auto pb-2 -mx-4 px-4 mb-2">
        <button
          onClick={() => setCatFilter("")}
          className={`px-3 py-1 text-[10px] uppercase tracking-wider rounded-full whitespace-nowrap ${!catFilter ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" : "bg-[hsl(var(--bg-2))] text-[hsl(var(--ink-soft))]"}`}
        >
          All
        </button>
        {CATEGORIES.map(c => (
          <button
            key={c}
            onClick={() => setCatFilter(c === catFilter ? "" : c)}
            className={`px-3 py-1 text-[10px] uppercase tracking-wider rounded-full whitespace-nowrap ${catFilter === c ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" : "bg-[hsl(var(--bg-2))] text-[hsl(var(--ink-soft))]"}`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Search input */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--ink-soft))]" />
        <input
          ref={inputRef}
          data-testid="item-search"
          value={q}
          onChange={e => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search item, SKU, or alias..."
          className="w-full pl-9 pr-8 py-3 text-sm border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] focus:outline-none focus:border-[hsl(var(--forest))]"
        />
        {q && (
          <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[hsl(var(--ink-soft))]">
            <X size={16} />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {open && filtered.length > 0 && (
        <div className="absolute z-30 left-0 right-0 mt-1 max-h-64 overflow-y-auto border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] shadow-lg">
          {filtered.map(item => (
            <button
              key={item.sku}
              data-testid={`pick-${item.sku}`}
              onClick={() => pick(item)}
              className="w-full text-left p-3 hover:bg-[hsl(var(--bg-2))] border-b border-[hsl(var(--rule))] last:border-b-0 flex items-center justify-between gap-2"
            >
              <div>
                <div className="font-semibold text-sm">{item.name}</div>
                <div className="text-xs text-[hsl(var(--ink-soft))]">
                  <span className="font-mono">{item.sku}</span>
                  {item.purchase_uom_label && item.base_per_purchase && (
                    <span className="ml-2 text-[hsl(var(--brass-2))]">1 {item.purchase_uom_label} = {item.base_per_purchase} {item.base_uom}</span>
                  )}
                </div>
              </div>
              <Plus size={16} className="text-[hsl(var(--forest))]" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
